import { Link } from "react-router-dom";
import { useReveal } from "@/hooks/useReveal";
import { UFJ_CATEGORIES } from "./data";

export default function UFJCategories({ pieces = [], onInquire }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();

  return (
    <section
      id="ufj-collection"
      data-testid="ufj-categories"
      className="relative bg-[#040404] py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,175,140,0.08)_0%,transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-20">
          <div className="lg:col-span-7">
            <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="ufj-categories-eyebrow">
              The Chapters of the Collection
            </p>
            <h2
              ref={title}
              className="pd-reveal mt-6 font-serif-display text-4xl md:text-5xl lg:text-6xl text-white tracking-[0.06em] leading-[1.08]"
              style={{ transitionDelay: "160ms" }}
              data-testid="ufj-categories-title"
            >
              Eight disciplines. <br />
              <span className="italic font-serif-body text-gold-gradient">One standard.</span>
            </h2>
          </div>
          <div ref={lead} className="pd-reveal lg:col-span-5" style={{ transitionDelay: "260ms" }}>
            <div className="hairline mb-6" />
            <p className="font-light text-white/60 text-base md:text-lg leading-relaxed tracking-wide">
              Each chapter is composed independently — released when a piece meets the maison's standard, not when a calendar dictates.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {UFJ_CATEGORIES.map((cat, i) => (
            <CategoryCard
              key={cat.key}
              category={cat}
              index={i}
              piece={pieces.find((p) => p.category_key === cat.key)}
              onInquire={onInquire}
            />
          ))}
        </div>

        <p className="mt-14 text-center text-white/40 text-[0.65rem] uppercase tracking-[0.4em]">
          All imagery shown above is concept imagery · Approved maison photography by inquiry
        </p>
      </div>
    </section>
  );
}

function CategoryCard({ category, index, piece, onInquire }) {
  const r = useReveal();
  return (
    <div
      ref={r}
      data-testid={`ufj-category-${category.key}`}
      style={{ transitionDelay: `${(index % 4) * 120}ms` }}
      className="pd-reveal group relative overflow-hidden border border-[var(--pd-border)] bg-black"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={category.image}
          alt={`${category.name} — concept imagery`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[2400ms] group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/20" />
        <div className="absolute top-3 left-3">
          <span className="text-[0.5rem] uppercase tracking-[0.4em] text-white/55 bg-black/55 backdrop-blur px-2.5 py-1">
            Concept
          </span>
        </div>
      </div>
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <p className="eyebrow text-white/60">Chapter {String(index + 1).padStart(2, "0")}</p>
        <h3
          className="mt-2 font-serif-display text-xl md:text-2xl text-white tracking-[0.1em] leading-tight"
          data-testid={`ufj-category-${category.key}-name`}
        >
          {category.name}
        </h3>
        <p className="mt-1 font-serif-body italic text-white/70 text-sm">{category.tagline}</p>
        <p className="mt-3 text-white/55 text-xs leading-relaxed line-clamp-3">
          {category.description}
        </p>

        {piece && (
          <Link
            to={`/ultra-fine-jewelry/${piece.slug}`}
            data-testid={`ufj-category-${category.key}-view`}
            className="mt-4 self-start text-[0.6rem] uppercase tracking-[0.36em] text-white hover:text-[var(--pd-gold)] transition-colors duration-500"
          >
            View the Piece
          </Link>
        )}
        <div className="mt-5 flex items-center justify-between">
          <span
            className="text-[0.6rem] uppercase tracking-[0.36em] text-[var(--pd-gold)]"
            data-testid={`ufj-category-${category.key}-price`}
          >
            Price Upon Request
          </span>
          <button
            onClick={() => onInquire?.(category)}
            data-testid={`ufj-category-${category.key}-cta`}
            className="text-[0.6rem] uppercase tracking-[0.36em] text-white/85 hover:text-[var(--pd-gold)] border-b border-white/40 hover:border-[var(--pd-gold)] pb-0.5 transition-all duration-500"
          >
            Inquire Privately →
          </button>
        </div>
      </div>
    </div>
  );
}
